import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Container, Spinner } from 'react-bootstrap';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import SchoolIcon from '@mui/icons-material/School';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Checkbox from '@mui/material/Checkbox';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import toast from 'react-hot-toast';
import userProgramsService from '../../services/userProgramsService';
import ReminderFormDropdown, { ReminderFormValues, ReminderProgramOption } from './components/ReminderFormDropdown';
import { ModalConfirm } from '../common/ModalConfirm';

/** API types for programs-manager/summary */
type RequireTypeItem = {
  type: string;
  status: string;
  targetDate?: string;
};

type SummaryReminder = {
  notes?: string;
  title: string;
  dueDate: string;
  complete: boolean;
};

type SummaryProgram = {
  /** programs-manager id (if provided by API) */
  id?: number;
  /** programs-manager id (alternate naming, if provided by API) */
  programManagerId?: number;
  programId?: number;
  programName: string;
  programPrimary: string;
  requiretype: RequireTypeItem[];
  reminders?: SummaryReminder[];
};

type SummaryTotals = {
  totalTracked: number;
  totalInProgress: number;
  totalUrgent: number;
};

type ProgramsManagerSummaryResponse = {
  programs: SummaryProgram[];
  totals: SummaryTotals;
};

const STATUS_DISPLAY: Record<string, { label: string; color: string }> = {
  completed: { label: 'Completed', color: '#16a34a' },
  submitted: { label: 'Submitted', color: '#2563eb' },
  requested: { label: 'Requested', color: '#2563eb' },
  'in progress': { label: 'In Progress', color: '#ea580c' },
  in_progress: { label: 'In Progress', color: '#ea580c' },
  'not started': { label: 'Not Started', color: '#dc2626' },
  not_started: { label: 'Not Started', color: '#dc2626' },
};

function formatTargetDate(iso?: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

function requirementTypeToDisplay(type: string): string {
  const map: Record<string, string> = {
    application: 'Application',
    essay: 'Essay',
    recommendation: 'Recommendation',
    transcript: 'Transcript',
    resume: 'Resume',
    interview: 'Interview',
  };
  return map[type?.toLowerCase()] ?? type;
}

const ICON_COLOR = '#2563eb';

const RequirementTypeIcon = ({ type }: { type: string }) => {
  const iconSx = { fontSize: 18, color: ICON_COLOR };
  switch (type) {
    case 'document':
    case 'Application':
      return <DescriptionIcon sx={iconSx} />;
    case 'essay':
    case 'Essay':
      return <EditIcon sx={iconSx} />;
    case 'recommendation':
    case 'Recommendation':
      return <ThumbUpIcon sx={iconSx} />;
    case 'transcript':
    case 'Transcript':
      return <SchoolIcon sx={iconSx} />;
    case 'interview':
    case 'Interview':
      return <RecordVoiceOverIcon sx={iconSx} />;
    case 'resume':
    case 'Resume':
      return <DescriptionIcon sx={iconSx} />;
    default:
      return <DescriptionIcon sx={iconSx} />;
  }
};

type RequirementRow = {
  id: number;
  programName: string;
  discipline: string;
  requirementType: string;
  requirementIcon: string;
  status: string;
  statusColor: string;
  targetDate: string;
};

type GroupedByProgram = {
  programId?: number;
  programName: string;
  discipline: string;
  requirements: RequirementRow[];
};

type ReminderRow = {
  title: string;
  notes?: string;
  dueDate: string;
  dueDateRaw: string;
  complete: boolean;
};

type GroupedRemindersByProgram = {
  programId?: number;
  programManagerId?: number;
  programName: string;
  discipline: string;
  reminders: ReminderRow[];
};

function typeToRequirementIcon(type: string): string {
  const t = type?.toLowerCase();
  if (t === 'application') return 'document';
  if (['essay', 'recommendation', 'transcript', 'interview', 'resume'].includes(t)) return t;
  return 'document';
}

const PROGRAMS_DETAIL_PATH = '/summer-programs/programs';

const ProgramsRequirements = () => {
  const history = useHistory();
  const [reminderToggles, setReminderToggles] = useState<Record<string, boolean>>({});
  const [summary, setSummary] = useState<ProgramsManagerSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reminderFormOpen, setReminderFormOpen] = useState(false);
  const [programManagerIdByProgramId, setProgramManagerIdByProgramId] = useState<Record<number, number>>({});
  const [expandedAggregatedKey, setExpandedAggregatedKey] = useState<string | null>(null);
  const [expandedRemindersKey, setExpandedRemindersKey] = useState<string | null>(null);
  const [expandedReminderRowKey, setExpandedReminderRowKey] = useState<string | null>(null);
  const [deleteReminderTarget, setDeleteReminderTarget] = useState<{ programId: number | undefined; index: number } | null>(null);

  const goToProgramDetails = (programId: number | undefined) => {
    if (programId != null) {
      history.push({ pathname: PROGRAMS_DETAIL_PATH, search: `?id=${programId}` });
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [res, managerListRes] = await Promise.all([
          userProgramsService.getProgramsManagerSummary(),
          userProgramsService.getProgramsManagerList(),
        ]);
        if (!cancelled && res) setSummary(res as unknown as ProgramsManagerSummaryResponse);
        if (!cancelled) {
          const list = Array.isArray(managerListRes) ? managerListRes : (managerListRes as any)?.data ?? [];
          const map: Record<number, number> = {};
          (list || []).forEach((row: any) => {
            const programId = row?.program_id ?? row?.program?.id;
            const id = row?.id;
            if (typeof programId === 'number' && typeof id === 'number') map[programId] = id;
          });
          setProgramManagerIdByProgramId(map);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load summary');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const reminderProgramOptions: ReminderProgramOption[] = useMemo(() => {
    if (!summary?.programs?.length) return [];
    return summary.programs
      .map((p) => {
        const programId = p.programId;
        if (typeof programId !== 'number') return null;
        const programManagerId = programManagerIdByProgramId[programId];
        if (typeof programManagerId !== 'number') return null;
        const currentReminders: ReminderFormValues[] = (p.reminders || []).map((r) => ({
          title: r.title,
          dueDate: r.dueDate,
          notes: r.notes ?? '',
          complete: r.complete ?? false,
        }));
        return {
          programId,
          programName: p.programName,
          programManagerId,
          currentReminders,
        };
      })
      .filter((x): x is ReminderProgramOption => x != null);
  }, [summary, programManagerIdByProgramId]);


  const groupedByProgram = useMemo(() => {
    if (!summary?.programs?.length) return [];
    const result: GroupedByProgram[] = [];
    let rowId = 0;
    summary.programs.forEach((prog) => {
      const requirements: RequirementRow[] = (prog.requiretype || []).map((req) => {
        const statusKey = (req.status || '').toLowerCase();
        const statusInfo = STATUS_DISPLAY[statusKey] ?? { label: req.status || '—', color: '#6b7280' };
        rowId += 1;
        return {
          id: rowId,
          programName: prog.programName,
          discipline: prog.programPrimary || '',
          requirementType: requirementTypeToDisplay(req.type),
          requirementIcon: typeToRequirementIcon(req.type),
          status: statusInfo.label,
          statusColor: statusInfo.color,
          targetDate: formatTargetDate(req.targetDate),
        };
      });
      if (requirements.length > 0) {
        result.push({
          programId: prog.programId,
          programName: prog.programName,
          discipline: prog.programPrimary ?? '',
          requirements,
        });
      }
    });
    return result;
  }, [summary]);

  const groupedRemindersByProgram = useMemo(() => {
    if (!summary?.programs?.length) return [];
    const result: GroupedRemindersByProgram[] = [];
    summary.programs.forEach((prog) => {
      const reminders = (prog.reminders || []).map((r) => ({
        title: r.title,
        notes: r.notes,
        dueDate: formatTargetDate(r.dueDate),
        dueDateRaw: r.dueDate,
        complete: r.complete,
      }));
      if (reminders.length > 0) {
        result.push({
          programId: prog.programId,
          programManagerId: prog.programManagerId ?? prog.id,
          programName: prog.programName,
          discipline: prog.programPrimary ?? '',
          reminders,
        });
      }
    });
    return result;
  }, [summary]);

  const reminderKey = (programId: number | undefined, index: number) => `${programId ?? ''}-${index}`;

  const isReminderChecked = (programId: number | undefined, index: number, complete: boolean): boolean => {
    const key = reminderKey(programId, index);
    return reminderToggles[key] ?? complete;
  };

  const handleToggleReminder = (programId: number | undefined, index: number, currentComplete: boolean) => {
    const key = reminderKey(programId, index);
    const nextComplete = !(reminderToggles[key] ?? currentComplete);
    setReminderToggles((prev) => ({ ...prev, [key]: nextComplete }));

    // Persist to programs-manager via PATCH when we have a manager id
    const group = groupedRemindersByProgram.find((g) => g.programId === programId);
    const programManagerId = group?.programManagerId;
    if (programManagerId == null) return;

    const updatedReminders = (group?.reminders || []).map((r, i) => ({
      title: r.title,
      dueDate: r.dueDateRaw,
      notes: r.notes,
      complete: i === index ? nextComplete : r.complete,
    }));

    userProgramsService
      .patchProgramsManager(programManagerId, { reminders: updatedReminders })
      .then(() => {
        // Keep UI consistent: update the grouped list source (summary) so next render reflects new complete
        setSummary((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            programs: prev.programs.map((p) => {
              const pid = p.programId;
              const pmid = (p as any).programManagerId ?? (p as any).id;
              if (pid !== programId && pmid !== programManagerId) return p;
              const reminders = (p.reminders || []).map((rr, i) => (i === index ? { ...rr, complete: nextComplete } : rr));
              return { ...p, reminders };
            }),
          };
        });
      })
      .catch(() => {
        // revert local toggle on failure (toast handled by interceptor)
        setReminderToggles((prev) => ({ ...prev, [key]: currentComplete }));
      });
  };

  const handleDeleteReminder = (programId: number | undefined, index: number) => {
    const group = groupedRemindersByProgram.find((g) => g.programId === programId);
    const programManagerId = group?.programManagerId;
    if (programManagerId == null) return;

    const updatedReminders = (group?.reminders || []).filter((_, i) => i !== index).map((r) => ({
      title: r.title,
      dueDate: r.dueDateRaw,
      notes: r.notes,
      complete: r.complete,
    }));

    userProgramsService
      .patchProgramsManager(programManagerId, { reminders: updatedReminders })
      .then(() => {
        setReminderToggles((prev) => {
          const next = { ...prev };
          (group?.reminders || []).forEach((_, i) => {
            delete next[reminderKey(programId, i)];
          });
          return next;
        });
        setSummary((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            programs: prev.programs.map((p) => {
              const pid = p.programId;
              const pmid = (p as any).programManagerId ?? (p as any).id;
              if (pid !== programId && pmid !== programManagerId) return p;
              const reminders = (p.reminders || []).filter((_, i) => i !== index);
              return { ...p, reminders };
            }),
          };
        });
        toast.success('Reminder deleted.');
      })
      .catch(() => {
        // Error handled by http interceptor
      });
  };

  return (
    <Container
      style={{
        width: '100%',
        maxWidth: '100%',
        padding: '1.25rem',
        margin: '0 auto',
      }}
    >
      <div style={{ width: '100%', maxWidth: '75rem', margin: '0 auto' }}>
        {/* Page Title and Subtitle */}
        <div className="row normal pt-4 pb-3">
          <div className="col-md-12">
            <h1
              style={{
                fontSize: '28px',
                fontWeight: 700,
                color: '#111827',
                margin: '0 0 8px 0',
                lineHeight: 1.2,
              }}
            >
              Requirements & Reminders
            </h1>
            <p
              style={{
                fontSize: '15px',
                color: '#6b7280',
                margin: 0,
                lineHeight: 1.5,
              }}
            >
              Your application progress across all programs
            </p>
          </div>
        </div>

        {/* Summary Cards */}
        {error && (
          <div className="row normal pb-3">
            <div className="col-12">
              <div style={{ padding: 12, backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: 8 }}>
                {error}
              </div>
            </div>
          </div>
        )}
        <div className="row normal g-3 pb-4">
          <div className="col-12 col-md-4">
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                padding: '20px 24px',
                position: 'relative',
                minHeight: '120px',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#2563eb',
                  backgroundColor: '#eff6ff',
                  padding: '4px 10px',
                  borderRadius: '9999px',
                }}
              >
                Active
              </span>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    backgroundColor: '#eff6ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <MenuBookIcon style={{ color: '#2563eb', fontSize: 28 }} />
                </div>
                <div>
                  <div style={{ fontSize: '32px', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                    {loading ? '—' : (summary?.totals?.totalTracked ?? 0)}
                  </div>
                  <div style={{ fontSize: '15px', color: '#6b7280', marginTop: 4 }}>Programs Tracked</div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                padding: '20px 24px',
                position: 'relative',
                minHeight: '120px',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#b45309',
                  backgroundColor: '#fef3c7',
                  padding: '4px 10px',
                  borderRadius: '9999px',
                }}
              >
                In Progress
              </span>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    backgroundColor: '#fef3c7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <AssignmentIcon style={{ color: '#d97706', fontSize: 28 }} />
                </div>
                <div>
                  <div style={{ fontSize: '32px', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                    {loading ? '—' : (summary?.totals?.totalInProgress ?? 0)}
                  </div>
                  <div style={{ fontSize: '15px', color: '#6b7280', marginTop: 4 }}>Requirements In Progress</div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                padding: '20px 24px',
                position: 'relative',
                minHeight: '120px',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#dc2626',
                  backgroundColor: '#fee2e2',
                  padding: '4px 10px',
                  borderRadius: '9999px',
                }}
              >
                Urgent
              </span>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    backgroundColor: '#fee2e2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <ScheduleIcon style={{ color: '#dc2626', fontSize: 28 }} />
                </div>
                <div>
                  <div style={{ fontSize: '32px', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                    {loading ? '—' : (summary?.totals?.totalUrgent ?? 0)}
                  </div>
                  <div style={{ fontSize: '15px', color: '#6b7280', marginTop: 4 }}>Upcoming Deadlines</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Aggregated Requirements */}
        <div className="row normal">
          <div className="col-12">
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                overflow: 'hidden',
              }}
            >
              {/* Section header */}
              <div
                style={{
                  padding: '20px 24px',
                  borderBottom: '1px solid #e5e7eb',
                }}
              >
                <h2
                  style={{
                    fontSize: '20px',
                    fontWeight: 700,
                    color: '#111827',
                    margin: '0 0 4px 0',
                  }}
                >
                  Requirements
                </h2>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                  All requirements across your tracked programs
                </p>
              </div>

              {/* PROGRAM NAME + accordion list */}
              <div style={{ padding: '16px 24px 24px' }}>
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#9ca3af',
                    letterSpacing: '0.05em',
                    marginBottom: 12,
                  }}
                >
                  PROGRAM NAME
                </div>
                {loading && (
                  <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading…
                  </div>
                )}
                {!loading && groupedByProgram.length === 0 && (
                  <div style={{ padding: 32, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
                    No requirements to show. Add programs to track their requirements.
                  </div>
                )}
                {!loading &&
                  groupedByProgram.map((group) => {
                    const groupKey = `${group.programName}-${group.discipline}-${group.programId ?? ''}`;
                    const isExpanded = expandedAggregatedKey === groupKey;
                    return (
                      <div
                        key={groupKey}
                        style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          marginBottom: 8,
                          overflow: 'hidden',
                          backgroundColor: '#fff',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '16px 20px',
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => setExpandedAggregatedKey((k) => (k === groupKey ? null : groupKey))}
                            style={{
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              border: 'none',
                              background: 'none',
                              cursor: 'pointer',
                              textAlign: 'left',
                              padding: 0,
                              minWidth: 0,
                            }}
                          >
                            {isExpanded ? (
                              <ExpandMoreIcon sx={{ fontSize: 24, color: '#6b7280', flexShrink: 0 }} />
                            ) : (
                              <ChevronRightIcon sx={{ fontSize: 24, color: '#6b7280', flexShrink: 0 }} />
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 700, fontSize: '16px', color: '#111827' }}>
                                {group.programName}
                              </div>
                              <div style={{ fontSize: '14px', color: '#6b7280', marginTop: 2 }}>
                                {group.discipline}
                              </div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              goToProgramDetails(group.programId);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#2563eb',
                              fontSize: '14px',
                              fontWeight: 500,
                              cursor: 'pointer',
                              padding: 0,
                              textDecoration: 'underline',
                              flexShrink: 0,
                            }}
                          >
                            View Details
                          </button>
                        </div>
                        {isExpanded && (
                          <div
                            style={{
                              borderTop: '1px solid #e5e7eb',
                              padding: '16px 20px 20px',
                              backgroundColor: '#fafafa',
                            }}
                          >
                            <div
                              style={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#9ca3af',
                                letterSpacing: '0.05em',
                                marginBottom: 12,
                              }}
                            >
                              REQUIREMENTS
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {group.requirements.map((row) => {
                                const isComplete =
                                  row.status.toLowerCase() === 'completed' || row.status.toLowerCase() === 'submitted';
                                return (
                                  <div
                                    key={row.id}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 12,
                                      padding: '12px 16px',
                                      backgroundColor: '#fff',
                                      borderRadius: '8px',
                                      border: '1px solid #e5e7eb',
                                    }}
                                  >
                                    {isComplete ? (
                                      <CheckCircleIcon
                                        sx={{ fontSize: 22, color: '#16a34a', flexShrink: 0 }}
                                        aria-hidden
                                      />
                                    ) : (
                                      <RadioButtonUncheckedIcon
                                        sx={{ fontSize: 22, color: '#d1d5db', flexShrink: 0 }}
                                        aria-hidden
                                      />
                                    )}
                                    <span style={{ flex: 1, fontSize: '14px', color: '#374151' }}>
                                      {row.requirementType}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        padding: '4px 10px',
                                        borderRadius: '9999px',
                                        backgroundColor: isComplete ? '#dcfce7' : '#f3f4f6',
                                        color: isComplete ? '#16a34a' : '#6b7280',
                                      }}
                                    >
                                      {isComplete ? 'Complete' : row.status}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>

        {/* Reminders & To Do's */}
        <div className="row normal pt-4 pb-4">
          <div className="col-12">
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                overflow: 'hidden',
              }}
            >
              {/* Section header */}
              <div
                style={{
                  padding: '20px 24px',
                  borderBottom: '1px solid #e5e7eb',
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <div>
                  <h2
                    style={{
                      fontSize: '20px',
                      fontWeight: 700,
                      color: '#111827',
                      margin: '0 0 4px 0',
                    }}
                  >
                    Personal Reminders
                  </h2>
                  <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                    Your upcoming tasks and to-dos
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button
                    type="button"
                    onClick={() => setReminderFormOpen((v) => !v)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      backgroundColor: '#2563eb',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 18px',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    <AddIcon sx={{ fontSize: 20 }} />
                    Add Reminder
                  </button>
                </div>
              </div>

              {reminderFormOpen && (
                <ReminderFormDropdown
                  programOptions={reminderProgramOptions}
                  onClose={() => setReminderFormOpen(false)}
                  onSavedForProgram={(programId, list) => {
                    setSummary((prev) => {
                      if (!prev) return prev;
                      return {
                        ...prev,
                        programs: prev.programs.map((p) => {
                          if (p.programId !== programId) return p;
                          return {
                            ...p,
                            reminders: list.map((r) => ({
                              title: r.title,
                              dueDate: r.dueDate,
                              notes: r.notes,
                              complete: r.complete ?? false,
                            })),
                          };
                        }),
                      };
                    });
                    setReminderFormOpen(false);
                  }}
                />
              )}

              {/* PROGRAM NAME + accordion list (same style as Aggregated Requirements) */}
              <div style={{ padding: '16px 24px 24px' }}>
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#9ca3af',
                    letterSpacing: '0.05em',
                    marginBottom: 12,
                  }}
                >
                  PROGRAM NAME
                </div>
                {loading && (
                  <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading…
                  </div>
                )}
                {!loading && groupedRemindersByProgram.length === 0 && (
                  <div style={{ padding: 32, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
                    No reminders to show. Add reminders from your program details.
                  </div>
                )}
                {!loading &&
                  groupedRemindersByProgram.map((group, groupIndex) => {
                    const groupKey = `reminder-${group.programId ?? group.programName}-${groupIndex}`;
                    const isExpanded = expandedRemindersKey === groupKey;
                    return (
                      <div
                        key={groupKey}
                        style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          marginBottom: 8,
                          overflow: 'hidden',
                          backgroundColor: '#fff',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '16px 20px',
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => setExpandedRemindersKey((k) => (k === groupKey ? null : groupKey))}
                            style={{
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              border: 'none',
                              background: 'none',
                              cursor: 'pointer',
                              textAlign: 'left',
                              padding: 0,
                              minWidth: 0,
                            }}
                          >
                            {isExpanded ? (
                              <ExpandMoreIcon sx={{ fontSize: 24, color: '#6b7280', flexShrink: 0 }} />
                            ) : (
                              <ChevronRightIcon sx={{ fontSize: 24, color: '#6b7280', flexShrink: 0 }} />
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 700, fontSize: '16px', color: '#111827' }}>
                                {group.programName}
                              </div>
                              <div style={{ fontSize: '14px', color: '#6b7280', marginTop: 2 }}>
                                {group.discipline}
                              </div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              goToProgramDetails(group.programId);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#2563eb',
                              fontSize: '14px',
                              fontWeight: 500,
                              cursor: 'pointer',
                              padding: 0,
                              textDecoration: 'underline',
                              flexShrink: 0,
                            }}
                          >
                            View Details
                          </button>
                        </div>
                        {isExpanded && (
                          <div
                            style={{
                              borderTop: '1px solid #e5e7eb',
                              padding: '16px 20px 20px',
                              backgroundColor: '#fafafa',
                            }}
                          >
                            <div
                              style={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#9ca3af',
                                letterSpacing: '0.05em',
                                marginBottom: 12,
                              }}
                            >
                              REMINDERS
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {group.reminders.map((reminder, reqIndex) => {
                                const checked = isReminderChecked(group.programId, reqIndex, reminder.complete);
                                const rowKey = `${group.programId ?? group.programName}-${reqIndex}`;
                                const isRowExpanded = expandedReminderRowKey === rowKey;
                                return (
                                  <div
                                    key={reminderKey(group.programId, reqIndex)}
                                    style={{
                                      border: '1px solid #e5e7eb',
                                      borderRadius: '8px',
                                      backgroundColor: '#fff',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    {/* Row: checkbox, Auto tag, title | program, due date, delete, chevron */}
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 12,
                                        padding: '12px 16px',
                                        cursor: 'pointer',
                                      }}
                                      onClick={() => setExpandedReminderRowKey((k) => (k === rowKey ? null : rowKey))}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault();
                                          setExpandedReminderRowKey((k) => (k === rowKey ? null : rowKey));
                                        }
                                      }}
                                      role="button"
                                      tabIndex={0}
                                    >
                                      <Checkbox
                                        checked={checked}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          handleToggleReminder(group.programId, reqIndex, reminder.complete);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{
                                          padding: 0,
                                          '& .MuiSvgIcon-root': { fontSize: 22 },
                                          color: '#9ca3af',
                                          '&.Mui-checked': { color: '#16a34a' },
                                        }}
                                      />
                                      <span
                                        style={{
                                          fontSize: '11px',
                                          fontWeight: 500,
                                          color: '#6b7280',
                                          padding: '2px 8px',
                                          borderRadius: '9999px',
                                          backgroundColor: '#f3f4f6',
                                          flexShrink: 0,
                                        }}
                                      >
                                        Auto
                                      </span>
                                      <span
                                        style={{
                                          flex: 1,
                                          fontSize: '14px',
                                          color: checked ? '#9ca3af' : '#111827',
                                          fontWeight: 500,
                                          textDecoration: checked ? 'line-through' : 'none',
                                        }}
                                      >
                                        {reminder.title}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: '11px',
                                          fontWeight: 500,
                                          padding: '2px 8px',
                                          borderRadius: '9999px',
                                          backgroundColor: checked ? '#dcfce7' : '#fef3c7',
                                          color: checked ? '#16a34a' : '#d97706',
                                          flexShrink: 0,
                                        }}
                                      >
                                        {checked ? 'Complete' : 'Incomplete'}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          goToProgramDetails(group.programId);
                                        }}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          color: '#2563eb',
                                          fontSize: '14px',
                                          fontWeight: 500,
                                          cursor: 'pointer',
                                          padding: 0,
                                          flexShrink: 0,
                                        }}
                                      >
                                        {group.programName}
                                      </button>
                                      <span style={{ fontSize: '14px', color: '#9ca3af', flexShrink: 0 }}>•</span>
                                      <span style={{ fontSize: '14px', color: '#6b7280', flexShrink: 0 }}>
                                        Due {reminder.dueDate}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setDeleteReminderTarget({ programId: group.programId, index: reqIndex });
                                        }}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          padding: '4px',
                                          cursor: 'pointer',
                                          color: '#dc2626',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          flexShrink: 0,
                                        }}
                                        title="Delete reminder"
                                        aria-label="Delete reminder"
                                      >
                                        <DeleteOutlineIcon sx={{ fontSize: '20px' }} />
                                      </button>
                                      {isRowExpanded ? (
                                        <KeyboardArrowUpIcon sx={{ fontSize: 20, color: '#6b7280', flexShrink: 0 }} />
                                      ) : (
                                        <KeyboardArrowDownIcon sx={{ fontSize: 20, color: '#6b7280', flexShrink: 0 }} />
                                      )}
                                    </div>
                                    {/* Dropdown below */}
                                    {isRowExpanded && (
                                      <div
                                        style={{
                                          borderTop: '1px solid #e5e7eb',
                                          padding: '16px',
                                          backgroundColor: '#fafafa',
                                        }}
                                      >
                                        {reminder.notes && (
                                          <div style={{ fontSize: '14px', color: '#374151' }}>
                                            {reminder.notes}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <ModalConfirm
        open={!!deleteReminderTarget}
        handleClose={() => setDeleteReminderTarget(null)}
        handleConfirm={() => {
          if (deleteReminderTarget) {
            handleDeleteReminder(deleteReminderTarget.programId, deleteReminderTarget.index);
            setDeleteReminderTarget(null);
          }
        }}
        title="Delete reminder"
        message="Are you sure to delete this reminder?"
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
        showWarningIcon
      />
    </Container>
  );
};

export default ProgramsRequirements;
